import React, { useEffect, useState } from "react";
import { Box, Container, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { cmsService, CMSService } from '../services/cms';
import { useThemeMode } from '../contexts/ThemeContext';
import PageTitle from './PageTitle';

const Services: React.FC = () => {
  const { t } = useTranslation();
  const { mode } = useThemeMode();
  const [services, setServices] = useState<CMSService[]>([]);
  const [loading, setLoading] = useState(true);

  // Define translated services
  const getTranslatedServices = (): CMSService[] => [
    {
      icon_name: 'LocalShipping',
      title: t('services.fallback.fastDelivery'),
      description: t('services.fallback.fastDeliveryDesc'),
      gradient_start: '#667eea',
      gradient_end: '#764ba2',
      shadow_color: 'rgba(102, 126, 234, 0.4)',
    },
    {
      icon_name: 'VerifiedUser',
      title: t('services.fallback.warrantyProtection'),
      description: t('services.fallback.warrantyProtectionDesc'),
      gradient_start: '#00d4ff',
      gradient_end: '#0099cc',
      shadow_color: 'rgba(0, 212, 255, 0.4)',
    },
    {
      icon_name: 'AccountBalance',
      title: t('services.fallback.flexibleFinancing'),
      description: t('services.fallback.flexibleFinancingDesc'),
      gradient_start: '#f093fb',
      gradient_end: '#f5576c',
      shadow_color: 'rgba(245, 87, 108, 0.4)',
    },
  ];

  const fetchServices = async () => {
    try {
      const { data, error } = await cmsService.getServices(true); // Only get active services
      if (!error && data && data.length > 0) {
        // Map CMS services with translations - use CMS styling but translate text
        const translatedCmsServices = data.map((service, index) => ({
          ...service,
          title: t(`services.cms.${index}.title`, { defaultValue: service.title }),
          description: t(`services.cms.${index}.description`, { defaultValue: service.description }),
        }));
        setServices(translatedCmsServices);
      } else {
        // Use fallback translated services
        setServices(getTranslatedServices());
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      // Fallback to translated services
      setServices(getTranslatedServices());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();

    // Subscribe to real-time updates
    const subscription = cmsService.subscribeToChanges('cms_services', () => {
      fetchServices();
    });

    return () => {
      cmsService.unsubscribe(subscription);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Get Material UI icon component by name
  const getIconComponent = (iconName: string) => {
    const IconComponent = (MuiIcons as any)[iconName] || MuiIcons.Help;
    return <IconComponent />;
  };

  return (
    <Box
      sx={{
        py: 8,
        backgroundColor: mode === 'light' ? "oklch(96.5% 0.026 102.212)" : "#0a0f1a",
        backdropFilter: "blur(20px)",
        mt: "auto",
      }}
    >
      <Container maxWidth="xl">
        <PageTitle
          text={t('services.title')}
          align="center"
          subtitle={t('services.subtitle')}
          size="medium"
          glowEffect
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: `repeat(${Math.min(services.length, 3)}, 1fr)`,
              },
              gap: 4,
            }}
          >
            {services.map((service, index) => (
            <Card
              key={index}
              sx={{
                height: "100%",
                // background: 'rgba(255, 255, 255, 0.98)',
                // backdropFilter: 'blur(20px)',
                // border: '1px solid rgba(0, 0, 0, 0.06)',
                // borderRadius: 3,
                overflow: "visible",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                // '&:hover': {
                //   transform: 'translateY(-8px)',
                //   boxShadow: `0 20px 40px ${service.shadowColor}`,
                //   '& .service-icon': {
                //     transform: 'rotate(10deg) scale(1.1)',
                //   },
                // },
                // '&::before': {
                //   content: '""',
                //   position: 'absolute',
                //   inset: -1,
                //   borderRadius: 'inherit',
                //   background: service.gradient,
                //   opacity: 0,
                //   transition: 'opacity 0.4s',
                //   zIndex: -1,
                // },
                // '&:hover::before': {
                //   opacity: 0.08,
                // },
              }}
            >
              <CardContent
                sx={{
                  p: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  className="service-icon"
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${service.gradient_start} 0%, ${service.gradient_end} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    boxShadow: `0 10px 30px ${service.shadow_color}`,
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "& svg": {
                      fontSize: 40,
                      color: "white",
                      position: "relative",
                      zIndex: 1,
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "-50%",
                      left: "-50%",
                      width: "200%",
                      height: "200%",
                      background:
                        "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)",
                      animation: "shimmer 3s infinite",
                    },
                    "@keyframes shimmer": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  {getIconComponent(service.icon_name)}
                </Box>

                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: mode === 'light' ? "#1a1a2e" : "#f0f4f8",
                    letterSpacing: "-0.02em",
                    textAlign: "center",
                  }}
                >
                  {service.title}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: mode === 'light' ? "rgba(0, 0, 0, 0.7)" : "rgba(240, 244, 248, 0.7)",
                    lineHeight: 1.7,
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  {service.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Services;
